<template>
	<div class="add">
		<template v-if="provider">

			<!-- Indicates the name of the provider and the patient profile
		 		 that the patient(s) will be enrolled into. If a provider has
			 	 multiple profiles, the @profile event will be triggered when the
			 	 default profile is changed (i.e. the first profile) -->
			<ProviderProfile v-if="!importing"
				:provider="provider"
				:profiles="profiles"
				@profile="setProfile">
			</ProviderProfile>

			<!-- Shows steps along the import process -->
			<ImportWizard v-show="importing"
				v-model="importStep"
				v-bind:count="patients.length"
				v-bind:parsing="parsing">
			</ImportWizard>

			<!-- Show results of creating the patients -->
			<div v-if="result" class="form result">
				<!-- Show any successfully created patients -->
				<PatientResultTable v-if="result.length > 0"
					:patients="result">
				</PatientResultTable>

				<!-- If there were any patients who could not be enrolled, then show a table with the
			 		 errors along with a button to submit corrected values -->
				<template v-if="patients && patients.length > 0">
					<p class="invalid">
						The following patients could not be added.
						Please fix the errors, then enroll again.
					</p>

					<PatientErrorTable
						:key="attempt"
						:patients="patients"
						:enrolling="creatingPatient">
					</PatientErrorTable>

					<div class="submit">
						<el-button type="primary"
							@click="enroll"
							v-bind:class="{ disabled: !canEnroll }">
							Enroll Patients
						</el-button>
					</div>
				</template>

				<!-- Otherwise (if there were no errors) show the buttons to complete enrollment -->
				<template v-else>
					<div class="submit">
						<el-button type="primary" plain
							@click="reset">
							Enroll More Patients
						</el-button>
						<el-button type="primary"
							@click="$router.push({ path: '/patient' })">
							Done
						</el-button>
					</div>
				</template>
			</div>

			<!-- If the user is on the step to import scheduled events -->
			<div v-else-if="importing && importStep == 1" class="form">
				<PatientScheduleTable
					:patients="patients"
					:events="events">
				</PatientScheduleTable>
			</div>

			<!-- If the user is on the step to import scheduled events -->
			<div v-else-if="importing && importStep == 2" class="form">
				<PatientConfirmationTable
					:patients="patients"
					:events="events">
				</PatientConfirmationTable>
			</div>

			<div v-else-if="profiles" class="form">
				<PatientFormTable
					:patients="patients"
					:show-error="showError"
					@upload="importRedirect">
				</PatientFormTable>

				<div class="submit" v-show="!importing">
					<el-button type="primary"
						@click="enroll"
						:class="{disabled: !canEnroll || enrolling}">
						Enroll Patients
					</el-button>
				</div>
			</div>
		</template>
		<template v-else>
			<Loading text="Loading provider..."></Loading>
		</template>

	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import Loading from '@/module/component/Loading'

import validPatient from '@/module/util/valid-patient'
import validEmail from '@/module/util/valid-email'
import validPhone from '@/module/util/valid-phone'

import ProviderProfile from './component/ProviderProfile'
import ImportWizard from './component/ImportWizard'

import Tables from './table/components'

import PatientRowHead from './head/Patient'
import PatientRow from './row/Patient'
import PatientResult from './row/PatientResult'

import ScheduleRowHead from './head/Schedule'
import ScheduleRow from './row/Schedule'

import Icons from './icons'

import parseFile from './util/parse-file'
// import parseCSV from './util/parse-csv'

function newPatient() {
	return { name: '', email: '', phone: '', provider: '', profile: [], schedule: [] }
}

function initialPatients() {
	return [
		{ data: newPatient() },
		{ data: newPatient() },
		{ data: newPatient() }
		// {
		// 	data: {
		// 		name: 'Test Patient',
		// 		email: 'test@testpatient.com',
		// 		event: [
		// 			{
		// 				id: 'v0_cm-follow-up-ibd-ibs-gi',
		// 				time: 1576872750733,
		// 				note: 'This was the note'
		// 			}
		// 		]
		// 	}
		// }
	]
}

const XLSX_SCRIPTS = [
	'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.8.0/jszip.js',
	'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.8.0/xlsx.js'
]

export default {
	name: 'add-patient',

	data() {
		return {
			attempt: 0,
			patients: initialPatients(),
			result: null,
			testing: false,
			importing: false,
			parsing: false,
			enrolling: false,
			importStep: 0,
			fileContent: '',
			xlsx: false,
			currentProfile: null,
			showError: false
		}
	},

	mounted() {
		// this.loadPatients()
		// this.loadXLSX()

		// TEMP
		// setTimeout(() => this.loadEvents(), 500)
		if(this.getProviderList.length > 0)
			this.setCurrentProvider(this.getProviderList[0].provider)
	},

	methods: {
		...mapActions('provider', [
			'batchCreatePatient',
			'loadPatients',
			'setCurrentProvider'
		]),
		...mapActions('content', [
			'loadContent'
		]),
		importRedirect() {
			this.$router.push({ to: '/import' })
		},
		setProfile(profile) {
			this.currentProfile = profile
		},
		loadXLSX() {
			return Promise.resolve()
			// return new Promise((resolve, reject) => {
			// 	let loaded = 0
			//
			// 	// Add all excel parsing scripts
			// 	XLSX_SCRIPTS.forEach((src) => {
			// 		let script = document.createElement('script')
		    // 		script.setAttribute('src', src)
			// 		script.onload = function() {
			// 			loaded++
			// 			if (loaded === XLSX_SCRIPTS.length) {
			// 				resolve()
			// 			}
			// 		}
		    // 		document.head.appendChild(script)
			// 	})
			// })
		},
		loadEvents() {
			if (this.profiles)
				return this.loadContent({
					type: 'event',
					id: [].concat.apply([], this.profiles.map((profile) => profile.event)).join(',')
				})
		},
		addRow() {
			this.patients.push({ data: newPatient() })
		},
		removeRow(index) {
			this.patients.splice(index, 1)
		},
		startImport() {
			this.importing = true
		},
		uploadFile() {
			this.$refs.file.click()
		},
		readFile(file) {

			if (file) {
				this.importing = true

				// Whether this is an Excel file
				var xlsx = (file.name.endsWith('.xls') || file.name.endsWith('.xlsx'))
				var csv = file.name.endsWith('.csv')

				// If this is not a valid file
				if (! xlsx && ! csv) {
					// TODO: show error dialog
					return
				}

				this.importing = true
				this.parsing = true
				this.patients = []

				this.loadContent({
					type: 'event',
					id: [].concat.apply([], this.profiles.map((profile) => profile.event)).join(',')
				})
				.then(() => {
					if (xlsx)
						this.readExcel(file)
					else
						this.readCSV(file)
				})
			}
		},
		readExcel(file) {
			var reader = new FileReader()
			reader.onload = (e) => {
				var workbook = XLSX.read(e.target.result, {
					type: 'binary'
				})
				var rows = []

				workbook.SheetNames.forEach((name) => {
					rows.push.apply(rows, XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1 }))
				})

				this.patients = parseFile(rows, this.getProvider, this.existingPatients, this.events)
				this.parsing = false
			}
			reader.onerror = function (evt) {
				console.log(evt)
			}

			reader.readAsBinaryString(file)
		},
		readCSV(file) {
			var reader = new FileReader()
			reader.onload = (e) => {
				// console.log(e.target.result)
				var rows = []
				// TODO: split and parse CSV file

				this.patients.splice(0, this.patients.length)
				this.patients = parseFile(rows, this.getProviderList, this.existingPatients, this.events)
			}
			reader.onerror = function (e) {
				console.log(e)
			}

			reader.readAsText(file, 'utf8')
		},
		enroll() {
			if (this.enrolling)
				return
			if(!this.canEnroll) {
				this.showError = true
				return
			}

			this.enrolling = true

			if (this.currentProfile == null && Array.isArray(this.profiles))
				this.currentProfile = this.profiles[0]

			// If no patients can be enrolled, or the enrollment is already in progress
			if (this.validPatients.length == 0 || this.creatingPatient)
				return
			let providerPatient = {}
			this.batchCreatePatient(this.validPatients.map((p) => {
				p.data.email = p.data.email.trim()
				p.data.phone = p.data.phone.trim()
				try {
					if(p.data.schedule && Array.isArray(p.data.schedule))
						providerPatient[p.data.email] = p.data.schedule[0].provider
					if(p.data.connect && Array.isArray(p.data.connect))
						providerPatient[p.data.email] = p.data.connect[0].provider
				} catch (error) {
					console.error('Problem getting provider for patient', error)
				}
				return p.data
			}))
			.then((result) => {
				this.attempt++
				try {
					result.patient = result.patient.map((patient) => ({
						...patient,
						providerEnroll: providerPatient[patient.email]
					}))
				} catch (error) {
					console.error('Problem mapping patient')
				}
				
				this.result = result.patient
				this.patients = result.error //.concat(this.clientError)
				this.enrolling = false
			})
			.catch((error) => {
				this.enrolling = false
				// TODO: show internal error
			})
		},
		confirmPatients() {
			this.importStep = 1
		},
		confirmSchedule() {
			this.importStep = 2
		},
		confirmImport() {
			this.importStep = 3
		},
		reset() {
			this.patients = initialPatients()
			this.result = null
		},
		done() {
			this.$router.push({ path: '/patient' })
		}
	},

	computed: {
		...mapState('provider', [
			'creatingPatient'
		]),
		...mapGetters('provider', [
			'getProvider',
			'getProviderName',
			'getProviderList',
			'getPatientList'
		]),
		...mapGetters('content', [
			'getContent'
		]),
		existingPatients() {
			return this.getPatientList
		},
		provider() {
			return this.getProvider
		},
		profiles() {
			if (this.provider)
				return this.provider.profile
			return null
		},
		events() {
			return [].concat.apply([], this.provider.profile.map((p) => p.event))
					 .map((id) => this.getContent('event', id))
					 .filter((e) => e != null)
		},
		validPatients() {
			return this.patients.filter((patient) => {
				// Patient should have name & email
				// Todo - add validity on email
				if(patient.data.name.length == 0)
					return false
				if (patient.data.email.length == 0 && patient.data.phone.length == 0)
					return false
				
				// Either the `schedule`(defines event info) or
				// `connect`(in case of no event defines provider and profile info) key should be present
				if(!patient.data.hasOwnProperty('connect') &&  !patient.data.hasOwnProperty('schedule'))
					return  false
				return true
					
					//(patient.data.name.length > 0 && patient.data.email.length > 0) &&
					
					//(patient.data.hasOwnProperty('schedule') || patient.data.hasOwnProperty('connect'))
			})
		},
		canEnroll() {
			return this.validPatients.length > 0 && !this.creatingPatient
		},
		clientError() {
			// Filter patients by filled out patients without valid data
			return this.patients.filter((patient) => (! validPatient(patient.data) && ! patient.error && (
				patient.data.name.length > 0 ||
				patient.data.email.length > 0 ||
				patient.data.phone.length > 0
			)))
			// Map to standard server errors
			.map((patient) => {
				let error = {}
				if (patient.data.name.trim().indexOf(' ') < 0)
					error.code = 'InvalidName'
				else if (! validEmail(patient.data.email) && ! validPhone(patient.data.phone))
					error.code = 'MissingEmailPhone'
				return { data: patient.data, error }
			})
		},
		isConfirmingPatients() {
			return this.importStep == 0
		},
		isConfirmingSchedule() {
			return this.importStep == 1
		}
	},

	components: {
		Loading,
		ProviderProfile,
		ImportWizard,
		...Tables,
		...Icons
	}
}
</script>

<style lang="scss" scoped>
@import "style.scss";
</style>
